/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 05.07.17.
 */

import {v4 as uuidv4} from "uuid";
import _ from "underscore";
/*
 * REST action
 * @typedef {Object} RestAction
 * @property {String} method Request method: "getCollection", "get", "post" ,"put", "del"
 * @property {String} id Request rsource id (used with methods "get", "put", "del")
 * @property {Object} resource Resource data (used with methods "post", "put")
 * @property {Number} firstRecord First record index (used with method "getCollection")
 * @property {Number} pageSize Max records to read  (used with method "getCollection")
 * @property {Object} filter Filter for records (used with method "getCollection")
 * @property {Array} sort Sort specifiecation (used with method "getCollection")
 * @property {String} state Action state: "waiting", "started", "finished"
 * @property {Date} timestamp Action creation timestamp
 * @property {Object} responseData Action response data (in case of "getCollection" - array of resource objects, in case of "get", "post", "put" - resource object)
 * @property {Number} responseRecordCount Total resource count (used only in case of "getCollection")
 * @property {Boolean} responseRecordCountEstimated True if total resource count is estimated number (not exact)
 * @property {Boolean} isError True if request failed
 * @property {String} error Error object if request failed
 * @property {Object} data Additional data attached to the action
 * @property {String} authToken Authentication token (if missing or null then global session token will be used, set to "" to clear authentication header)
 */

/**
 * Base action class
 */
class RestAction {
    clone() {
        let newObject = Object.assign(Object.create(Object.getPrototypeOf(this)), this);
        if (newObject.resource) newObject.resource = _.clone(newObject.resource);
        if (newObject.filter) newObject.filter = _.clone(newObject.filter);
        if (newObject.sort) newObject.sort = _.clone(newObject.sort);
        if (newObject.responseData) newObject.responseData = _.clone(newObject.responseData);
        if (newObject.error) newObject.error = _.clone(newObject.error);
        return newObject;
    }

    isEqual(action) {
        let is =
            this.method == action.method &&
            this.id == action.id &&
            (this.resource == action.resource || !!_.isEqual(this.resource, action.resource)) &&
            this.firstRecord == action.firstRecord &&
            this.pageSize == action.pageSize &&
            (this.filter == action.filter || !!_.isEqual(this.filter, action.filter)) &&
            (this.sort == action.sort || !!_.isEqual(this.sort, action.sort));
        return is;
    }

    /**
     * Create new action
     * @param {string} method Method name
     * @param {number} id Resource ID
     * @param {Object} resource Resource record
     */
    constructor(method, id=null, resource=null) {
        /**
         * Timestamp
         * @type {Date}
         */
        this.timestamp = new Date();
        /**
         * Action ID
         * @type {string}
         */
        this.actionId = "urn:uuid:" + uuidv4();
        /**
         * Action method. One of: : "getCollection", "get", "post" ,"put", "del"
         * @type {String}
         */
        this.method = method;
        /**
         * Action request resource id
         * @type {Number}
         */
        this.id = id;
        /**
         * Action request resource data
         * @type {Object}
         */
        this.resource = resource;

        /**
         * Action request first record index
         * @type {number}
         */
        this.firstRecord = 0;
        /**
         * Action request page size
         * @type {number}
         */
        this.pageSize = 0;
        /**
         * Action filter
         * @type {Object}
         */
        this.filter = null;
        /**
         * Action sort specification
         * @type {Array}
         */
        this.sort = null;
        /**
         * Action state: "waiting", "started", "finished"
         * @type {string}
         */
        this.state = "waiting";

        /**
         * Response data
         * @type {Object}
         */
        this.responseData = null;
        /**
         * Links for data
         * @type {Link[]}
         */
        this.responseLinks = null;
        /**
         * Resource count
         * @type {number}
         */
        this.responseRecordCount = 0;
        /**
         * Is the resource count exact number or estimated number
         * @type {boolean}
         */
        this.responseRecordCountEstimated = null;
        /**
         * Is error response
         * @type {boolean}
         */
        this.isError = false;
        /**
         * Error object
         * @type {RestApiErrorResponse}
         */
        this.error = null;
        /**
         * Additional data not interpreted by action itself
         * @type {Object}
         */
        this.data = null;
        /**
         * Authentication token (if missing or null then global session token will be used, set to "" to clear authentication header)
         * @type {string}
         */
        this.authToken = null;
    }
}

class GetCollectionRestAction extends RestAction {
    /**
     *
     * @param {BaseRestStoreState} state
     * @param {RestAction} params
     */
    constructor(state, params) {
        super("getCollection");
        if (state) {
            this.firstRecord = state.firstRecord;
            this.pageSize = state.pageSize;
            this.sort = state.sort;
            this.filter = state.filter;
        }
        if (params) {
            if (_.has(params, "firstRecord")) this.firstRecord = params.firstRecord;
            if (_.has(params, "pageSize")) this.pageSize = params.pageSize;
            if (_.has(params, "sort")) this.sort = params.sort;
            if (_.has(params, "filter")) this.filter = params.filter;
            if (_.has(params, "data")) this.data = params.data;
        }
    }
}

class GetRestAction extends RestAction {
    constructor(id) {
        super("get", id);
    }
}

class PostRestAction extends RestAction {
    constructor(resource) {
        super("post", null, resource);
    }
}

class PutRestAction extends RestAction {
    constructor(id, resource) {
        super("put", id, resource);
    }
}

class DelRestAction extends RestAction {
    constructor(id) {
        super("del", id);
    }
}

class SaveRestAction extends RestAction {
    constructor(data) {
        super("save");
    }
}

class PatchEditAction extends RestAction {
    constructor(resource) {
        super("patch-edit", null, resource);
    }
}

export {
    RestAction,
    GetCollectionRestAction,
    GetRestAction,
    PostRestAction,
    PutRestAction,
    DelRestAction,
    SaveRestAction
};
